// Sales Navigator Import — ingests a prospect list the OWNER manually exported
// from Sales Navigator (provenance salesnav_export, SANCTIONED). THE HONEST
// PIPELINE: this surface NEVER sends and NEVER scrapes. It only creates
// compliant leads from a list the owner already assembled. Leads with a
// LinkedIn handle then flow into the /ops/social approval queue as DRAFT
// connect-messages (the owner sends them by hand on LinkedIn); leads with a
// verified email can be emailed automatically once email outreach is enabled.
//
// Paste path (the simplest reliable one — Sales Nav users copy/paste rows) OR a
// .csv file read client-side via FileReader into the same flow. One Import
// button POSTs { csv_text } to /api/ops/prospects/import-salesnav and renders
// the imported / duplicates / skipped summary. The table below polls
// GET /api/ops/prospects (~15s) and refetches on a successful import.
import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link as RouterLink } from "react-router-dom";
import {
  Upload,
  FileText,
  Linkedin,
  CheckCircle2,
  CopyX,
  AlertTriangle,
  Users,
  Inbox,
  ExternalLink,
  Sparkles,
} from "lucide-react";

import MockBadge from "@/components/MockBadge";
import { StrategyBriefDrawer } from "@/components/StrategyBrief";
import { prospects } from "@/lib/api";
import { fmtTime } from "@/lib/format";

const BTN_PRIMARY_STYLE = {
  background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))",
  color: "rgb(252,249,243)",
};
const BTN_SECONDARY_STYLE = {
  background: "rgba(244,239,230,0.9)",
  border: "1px solid rgba(190,176,152,0.6)",
  color: "rgb(38,31,24)",
};

// The backend summary may name the same number a few different ways depending
// on the route version — read defensively so the card never paints a blank.
const num = (...vals) => {
  for (const v of vals) if (typeof v === "number") return v;
  return 0;
};

// Skipped rows can come back as plain strings or { reason, row, name } objects.
function skipReason(s) {
  if (s == null) return "skipped";
  if (typeof s === "string") return s;
  const label = s.name || s.full_name || s.email || s.row || s.line;
  const reason = s.reason || s.detail || s.message || "skipped";
  return label ? `${label} — ${reason}` : String(reason);
}

// One prospect row, tolerant of the field names the leads API is likely to use.
function ProspectRow({ p, onStrategy }) {
  const name = p.full_name || p.name || "—";
  const company = p.company || p.organization || "—";
  const title = p.title || p.headline || p.job_title || "—";
  const email = p.email || null;
  const linkedin =
    p.linkedin_url ||
    p.enrichment?.linkedin_url ||
    p.linkedin ||
    null;
  const imported = p.created_at || p.imported_at || p.updated_at || null;
  return (
    <tr
      data-testid={`prospect-row-${p.id || name}`}
      className="border-t border-[rgb(var(--ln))] hover:bg-[rgb(236,229,216)]"
    >
      <td className="px-3 py-2.5 mono text-[12px] text-[rgb(var(--fg))] align-top">
        <span className="truncate block max-w-[200px]" title={name}>
          {name}
        </span>
      </td>
      <td className="px-3 py-2.5 mono text-[12px] text-[rgb(var(--dim))] align-top">
        <span className="truncate block max-w-[180px]" title={company}>
          {company}
        </span>
      </td>
      <td className="px-3 py-2.5 mono text-[12px] text-[rgb(var(--dim))] align-top">
        <span className="truncate block max-w-[220px]" title={title}>
          {title}
        </span>
      </td>
      <td className="px-3 py-2.5 mono text-[12px] align-top">
        {linkedin ? (
          <a
            href={linkedin}
            target="_blank"
            rel="noreferrer"
            data-testid={`prospect-linkedin-${p.id || name}`}
            className="inline-flex items-center gap-1.5 text-[rgb(var(--gold))] hover:underline"
            title={linkedin}
          >
            <Linkedin size={12} className="shrink-0" />
            <span>Profile</span>
          </a>
        ) : (
          <span className="text-[rgb(var(--muted-2))]">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 mono text-[12px] text-[rgb(var(--dim))] align-top">
        <span className="truncate block max-w-[200px]" title={email || ""}>
          {email || "—"}
        </span>
      </td>
      <td className="px-3 py-2.5 mono text-[11px] text-[rgb(var(--muted-2))] align-top whitespace-nowrap">
        {imported ? fmtTime(imported) : "—"}
      </td>
      <td className="px-3 py-2.5 align-top whitespace-nowrap text-right">
        <button
          type="button"
          onClick={() => onStrategy?.(p)}
          data-testid={`prospect-strategy-btn-${p.id || name}`}
          className="mono inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] hover:opacity-90"
          style={{
            background: "rgba(169,130,28,0.10)",
            border: "1px solid rgba(169,130,28,0.35)",
            color: "rgb(var(--gold))",
            borderRadius: 2,
          }}
          title="Generate an engagement-strategy brief for this person"
        >
          <Sparkles size={11} className="shrink-0" />
          Strategy
        </button>
      </td>
    </tr>
  );
}

export default function Prospects() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  // The prospect whose engagement-strategy drawer is open (null = closed).
  const [strategyLead, setStrategyLead] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-prospects"],
    queryFn: () => prospects.list(),
    refetchInterval: 15000,
    retry: false,
  });

  // The list API may return a bare array or { prospects | leads | items: [...] }.
  const rows = Array.isArray(data)
    ? data
    : data?.prospects || data?.leads || data?.items || [];

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      // Read the file text client-side into the SAME textarea flow — Import then
      // uses whatever is in csvText regardless of paste-vs-file.
      setCsvText(typeof reader.result === "string" ? reader.result : "");
    };
    reader.onerror = () => toast.error("Could not read that file — try pasting the rows instead.");
    reader.readAsText(file);
  };

  const clearFile = () => {
    setFileName(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onImport = async () => {
    const text = csvText.trim();
    if (!text) {
      toast.error("Paste your Sales Navigator rows (or choose a .csv) first.");
      return;
    }
    setBusy(true);
    try {
      const r = await prospects.import(text);
      setSummary(r);
      const imported = num(r?.imported, r?.created, r?.added);
      toast.success(
        imported === 1 ? "1 prospect imported" : `${imported} prospects imported`,
      );
      // Surface the new rows in the table immediately.
      qc.invalidateQueries({ queryKey: ["ops-prospects"] });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Import failed — check the CSV format and try again.",
      );
    }
    setBusy(false);
  };

  const importedN = num(summary?.imported, summary?.created, summary?.added);
  const duplicatesN = num(summary?.duplicates, summary?.duplicate, summary?.skipped_duplicates);
  const skippedList = summary?.skipped_reasons || summary?.skipped_rows || summary?.errors || [];
  const skippedN = num(summary?.skipped) || (Array.isArray(skippedList) ? skippedList.length : 0);

  return (
    <div data-testid="page-prospects" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="pj-label mb-2">· Outreach · Sales Navigator import</div>
          <h1 className="font-display text-[32px] md:text-[40px] leading-[1.02]">Prospects</h1>
        </div>
        <div className="shrink-0">
          <MockBadge />
        </div>
      </div>

      {/* Honest-pipeline blurb — what import does and emphatically does NOT do. */}
      <p
        className="mono text-[12px] text-[rgb(var(--dim))] mb-6 max-w-3xl"
        data-testid="prospects-blurb"
      >
        Paste a list you exported from Sales Navigator. LinkedIn-handle prospects become draft
        connect-messages in the{" "}
        <RouterLink to="/ops/social" className="text-[rgb(var(--gold))] hover:underline">
          Approval Queue
        </RouterLink>{" "}
        (you send on LinkedIn). Prospects with verified emails can be emailed automatically once
        email outreach is enabled. We never scrape LinkedIn.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        {/* Import panel */}
        <div className="pj-card p-5" data-testid="prospects-import-card">
          <div className="flex items-center gap-2 mb-3">
            <Upload size={14} className="text-[rgb(var(--gold))] shrink-0" />
            <h2 className="mono text-[13px] uppercase tracking-[0.12em] text-[rgb(var(--fg))]">
              Import a list
            </h2>
          </div>

          <textarea
            data-testid="prospects-csv-textarea"
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={
              "Paste CSV rows here, e.g.\nName,Company,Title,LinkedIn URL,Email\nJane Doe,Acme Jets,VP Travel,https://linkedin.com/in/janedoe,jane@acme.com"
            }
            spellCheck={false}
            className="w-full mono text-[12px] p-3 resize-y"
            style={{
              minHeight: 180,
              background: "rgba(244,239,230,0.6)",
              border: "1px solid rgb(var(--ln))",
              color: "rgb(var(--fg))",
              borderRadius: 2,
            }}
          />

          <div className="mt-3 flex flex-wrap items-center gap-3">
            {/* File path reads into the SAME csvText via FileReader. */}
            <label
              className="mono inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] cursor-pointer hover:opacity-90"
              style={BTN_SECONDARY_STYLE}
              data-testid="prospects-file-label"
            >
              <FileText size={11} />
              Choose .csv
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                onChange={onFile}
                data-testid="prospects-file-input"
                className="hidden"
              />
            </label>

            {fileName && (
              <span
                className="mono text-[11px] text-[rgb(var(--dim))] inline-flex items-center gap-2 min-w-0"
                data-testid="prospects-file-name"
              >
                <span className="truncate max-w-[180px]" title={fileName}>
                  {fileName}
                </span>
                <button
                  type="button"
                  onClick={clearFile}
                  className="text-[rgb(var(--muted-2))] hover:text-[rgb(var(--fg))]"
                  aria-label="Clear file"
                >
                  <CopyX size={12} />
                </button>
              </span>
            )}

            <button
              type="button"
              data-testid="prospects-import-btn"
              onClick={onImport}
              disabled={busy || !csvText.trim()}
              className="mono inline-flex items-center gap-1.5 px-4 py-1.5 text-[10px] uppercase tracking-[0.12em] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
              style={BTN_PRIMARY_STYLE}
            >
              <Upload size={11} />
              {busy ? "Importing…" : "Import"}
            </button>
          </div>

          <p className="mono text-[10px] text-[rgb(var(--muted-2))] mt-3">
            Provenance is recorded as <code>salesnav_export</code> (sanctioned). Nothing is sent on
            import.
          </p>
        </div>

        {/* Result summary card — only after an import returns. */}
        <div className="pj-card p-5 flex flex-col" data-testid="prospects-result-card">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={14} className="text-[rgb(var(--gold))] shrink-0" />
            <h2 className="mono text-[13px] uppercase tracking-[0.12em] text-[rgb(var(--fg))]">
              Last import
            </h2>
          </div>

          {!summary ? (
            <div className="flex-1 flex items-center justify-center mono text-[12px] text-[rgb(var(--muted-2))] py-8 text-center">
              Import a list to see a summary here.
            </div>
          ) : (
            <div data-testid="prospects-summary">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <div className="font-display text-[28px] leading-none" style={{ color: "rgb(32,138,78)" }}>
                    {importedN}
                  </div>
                  <div className="pj-label mt-1">Imported</div>
                </div>
                <div>
                  <div className="font-display text-[28px] leading-none" style={{ color: "rgb(176,116,18)" }}>
                    {duplicatesN}
                  </div>
                  <div className="pj-label mt-1">Duplicates</div>
                </div>
                <div>
                  <div className="font-display text-[28px] leading-none" style={{ color: "rgb(196,52,44)" }}>
                    {skippedN}
                  </div>
                  <div className="pj-label mt-1">Skipped</div>
                </div>
              </div>

              {Array.isArray(skippedList) && skippedList.length > 0 && (
                <div data-testid="prospects-skipped-reasons">
                  <div className="mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--dim))] mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle size={11} className="shrink-0" style={{ color: "rgb(196,52,44)" }} />
                    Skipped rows
                  </div>
                  <ul className="mono text-[11px] text-[rgb(var(--dim))] space-y-1 max-h-40 overflow-y-auto">
                    {skippedList.map((s, i) => (
                      <li key={i} className="break-words">
                        · {skipReason(s)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mt-auto pt-4">
            <RouterLink
              to="/ops/social"
              data-testid="prospects-goto-social"
              className="mono inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] hover:opacity-90"
              style={BTN_SECONDARY_STYLE}
            >
              <ExternalLink size={11} />
              Open Approval Queue
            </RouterLink>
            <p className="mono text-[10px] text-[rgb(var(--muted-2))] mt-2">
              <code>SOCIAL_OUTREACH</code> must be enabled for draft connect-messages to generate.
            </p>
          </div>
        </div>
      </div>

      {/* Recently imported prospects */}
      <div className="flex items-center gap-2 mb-3">
        <Users size={14} className="text-[rgb(var(--gold))] shrink-0" />
        <h2 className="mono text-[13px] uppercase tracking-[0.12em] text-[rgb(var(--fg))]">
          Recently imported
        </h2>
      </div>

      {error && (
        <div
          className="pj-card p-6 mono text-[12px] text-[rgb(196,52,44)]"
          data-testid="prospects-load-error"
        >
          Could not load prospects (admin / ops roles only).
        </div>
      )}
      {isLoading && !data && (
        <div className="pj-card p-6 mono text-[12px] text-[rgb(var(--dim))]">Loading prospects…</div>
      )}

      {data && rows.length === 0 && (
        <div
          className="pj-card p-8 flex flex-col items-center justify-center text-center"
          data-testid="prospects-empty"
        >
          <Inbox size={20} className="text-[rgb(var(--muted-2))] mb-2" />
          <div className="mono text-[12px] text-[rgb(var(--dim))]">
            No prospects yet — import a Sales Navigator list above.
          </div>
        </div>
      )}

      {data && rows.length > 0 && (
        <div className="pj-card overflow-x-auto" data-testid="prospects-table">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                {["Name", "Company", "Title", "LinkedIn", "Email", "Imported", "Strategy"].map(
                  (h) => (
                    <th
                      key={h}
                      className={`px-3 py-2.5 mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--muted-2))] font-normal ${
                        h === "Strategy" ? "text-right" : ""
                      }`}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <ProspectRow
                  key={p.id || p.full_name || p.email}
                  p={p}
                  onStrategy={setStrategyLead}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-person engagement-strategy brief drawer (advisory, human-executed). */}
      {strategyLead && (
        <StrategyBriefDrawer lead={strategyLead} onClose={() => setStrategyLead(null)} />
      )}
    </div>
  );
}
